import React, { useState } from "react";

import {
  FaBoxOpen,
  FaCheckCircle,
  FaTimesCircle,
  FaImage,
  FaVideo,
  FaFileAlt,
} from "react-icons/fa";

import { approveReturnRequest, declineReturnRequest } from "../services/api";

export default function ReturnRequestDetails({ returnRequest, onUpdated }) {
  const [processing, setProcessing] = useState(false);
  const [showDeclineBox, setShowDeclineBox] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  console.log(returnRequest, "returnRequest 22");
  if (!returnRequest) return null;

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // =====================================================
  // FORMAT AMOUNT
  // =====================================================

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  // =====================================================
  // STATUS CLASS
  // =====================================================

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "return_requested":
        return "bg-yellow-100 text-yellow-700";

      case "approved":
        return "bg-green-100 text-green-700";

      case "declined":
        return "bg-red-100 text-red-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // =====================================================
  // ITEMS
  // =====================================================

  const items = returnRequest.items || [];

  // =====================================================
  // TOTAL RETURN AMOUNT
  // =====================================================

  const totalReturnAmount = items.reduce((total, item) => {
    return total + Number(item.price || 0) * Number(item.quantity || 0);
  }, 0);

  // =====================================================
  // APPROVE
  // =====================================================

  const handleApprove = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to approve this return request?",
    );

    if (!confirmed) return;

    try {
      setProcessing(true);

      const response = await approveReturnRequest(returnRequest._id, "");

      if (response?.success) {
        alert("Return request approved successfully.");

        if (onUpdated) {
          onUpdated({
            ...returnRequest,
            status: "approved",
          });
        }
      } else {
        alert(response?.message || "Failed to approve return request.");
      }
    } catch (error) {
      console.error("Approve return request error:", error);

      alert(
        error?.response?.data?.message || "Failed to approve return request.",
      );
    } finally {
      setProcessing(false);
    }
  };

  // =====================================================
  // DECLINE
  // =====================================================

  const handleDecline = async () => {
    const reason = declineReason.trim();

    if (!reason) {
      alert("Please provide a reason for declining the return.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to decline this return request?",
    );

    if (!confirmed) return;

    try {
      setProcessing(true);

      const response = await declineReturnRequest(returnRequest._id, reason);

      if (response?.success) {
        alert("Return request declined successfully.");

        if (onUpdated) {
          onUpdated({
            ...returnRequest,
            status: "declined",
            adminNote: reason,
          });
        }

        setShowDeclineBox(false);
        setDeclineReason("");
      } else {
        alert(response?.message || "Failed to decline return request.");
      }
    } catch (error) {
      console.error("Decline return request error:", error);

      alert(
        error?.response?.data?.message || "Failed to decline return request.",
      );
    } finally {
      setProcessing(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="bg-gray-50 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <FaFileAlt className="text-[#E87932]" />

            <h3 className="font-semibold text-gray-900">Return Request</h3>

            <span
              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(
                returnRequest.status,
              )}`}
            >
              {String(returnRequest.status || "-")
                .replaceAll("_", " ")
                .toUpperCase()}
            </span>
          </div>

          <p className="text-xs text-gray-500 mt-1">
            Return Request ID: {returnRequest._id?.toString() || "-"}
          </p>
        </div>
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div className="p-5">
        {/* =====================================================
            RETURN INFORMATION
        ===================================================== */}

        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
          <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <FaFileAlt className="text-[#E87932]" />
              Return Information
            </h4>
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <p className="text-xs text-gray-500">Return Request Date</p>

              <p className="font-medium text-gray-800 mt-1">
                {formatDate(returnRequest.createdAt)}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Order ID</p>

              <p className="font-medium text-gray-800 mt-1 break-all">
                {returnRequest.orderId?.toString() || "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Return Amount</p>

              <p className="font-semibold text-light_primary mt-1">
                ₹{formatAmount(totalReturnAmount)}
              </p>
            </div>
          </div>

          {/* REASON */}

          <div className="px-5 pb-5">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Return Reason</p>

              <p className="font-medium text-gray-800 capitalize">
                {returnRequest.reason || "-"}
              </p>
            </div>
          </div>

          {/* ADDITIONAL REASON */}

          <div className="px-5 pb-5">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Additional Details</p>

              <p className="font-medium text-gray-800 whitespace-pre-wrap">
                {returnRequest.additionalReason ||
                  "No additional details provided."}
              </p>
            </div>
          </div>

          {/* ADMIN NOTE */}

          {returnRequest.adminNote && (
            <div className="px-5 pb-5">
              <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                <p className="text-xs text-red-600 mb-1">Admin Note</p>

                <p className="font-medium text-red-800 whitespace-pre-wrap">
                  {returnRequest.adminNote}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* =====================================================
            PRODUCTS
        ===================================================== */}

        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
          <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <FaBoxOpen className="text-[#E87932]" />
              Products Being Returned
            </h4>
          </div>

          <div className="p-5 space-y-5">
            {items.length === 0 ? (
              <p className="text-gray-500 text-sm">No products found.</p>
            ) : (
              items.map((item, index) => (
                <div
                  key={`${item.productId}-${index}`}
                  className="flex gap-4 items-center pb-5 border-b border-gray-100 last:border-b-0 last:pb-0"
                >
                  {/* IMAGE */}

                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                    {item.image?.[0] ? (
                      <img
                        src={item.image[0]}
                        alt={item.title || "Product"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FaBoxOpen className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* DETAILS */}

                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">
                      {item.title || "Product"}
                    </h4>

                    <p className="text-sm text-gray-500 mt-1">
                      ₹{formatAmount(item.price)} × {item.quantity}
                    </p>

                    <p className="text-sm font-semibold text-gray-800 mt-1">
                      Total: ₹
                      {formatAmount(
                        Number(item.price || 0) * Number(item.quantity || 0),
                      )}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* =====================================================
            IMAGES
        ===================================================== */}

        {returnRequest.images?.length > 0 && (
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <FaImage className="text-[#E87932]" />
                Return Images
              </h4>
            </div>

            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              {returnRequest.images.map((image, index) => (
                <a
                  key={index}
                  href={image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src={image}
                    alt={`Return ${index + 1}`}
                    className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* =====================================================
            VIDEOS
        ===================================================== */}

        {returnRequest.videos?.length > 0 && (
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <FaVideo className="text-[#E87932]" />
                Return Videos
              </h4>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {returnRequest.videos.map((video, index) => (
                <video
                  key={index}
                  src={video}
                  controls
                  className="w-full rounded-lg border border-gray-200"
                />
              ))}
            </div>
          </div>
        )}

        {/* =====================================================
            DECLINE REASON
        ===================================================== */}

        {showDeclineBox && returnRequest.status === "return_requested" && (
          <div className="border border-red-200 bg-red-50 rounded-xl p-5 mb-6">
            <h4 className="font-semibold text-red-800 mb-3">
              Reason for Declining
            </h4>

            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter reason for declining this return request..."
              rows={4}
              className="text-black w-full border border-red-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-red-200 resize-none"
              disabled={processing}
            />

            <p className="text-xs text-red-600 mt-2">
              This reason is mandatory and will be sent to the customer by
              email.
            </p>
          </div>
        )}

        {/* =====================================================
            ADMIN ACTIONS
        ===================================================== */}

        {returnRequest.status === "return_requested" && (
          <div className="border-t border-gray-200 pt-5 flex flex-col sm:flex-row justify-end gap-3">
            {!showDeclineBox ? (
              <button
                type="button"
                onClick={() => setShowDeclineBox(true)}
                disabled={processing}
                className="px-5 py-2.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 font-medium transition disabled:opacity-50"
              >
                <FaTimesCircle className="inline mr-2" />
                Decline Return
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeclineBox(false);
                    setDeclineReason("");
                  }}
                  disabled={processing}
                  className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={processing}
                  className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition disabled:opacity-50"
                >
                  {processing ? "Processing..." : "Confirm Decline"}
                </button>
              </>
            )}

            {!showDeclineBox && (
              <button
                type="button"
                onClick={handleApprove}
                disabled={processing}
                className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition disabled:opacity-50"
              >
                <FaCheckCircle className="inline mr-2" />

                {processing ? "Processing..." : "Approve Return"}
              </button>
            )}
          </div>
        )}

        {/* =====================================================
            PROCESSED MESSAGE
        ===================================================== */}

        {returnRequest.status !== "return_requested" && (
          <div className="border-t border-gray-200 pt-5">
            <p className="text-sm text-gray-500 text-right">
              This return request has already been{" "}
              <span className="font-semibold capitalize">
                {returnRequest.status}
              </span>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
